import type { InstantConfigRepository } from "instant-config";
import {
  AdEnginePlugin,
  type AuctionData,
  type Hooks,
} from "../../core/AdEnginePlugin";
import { PrebidPlugin } from "./PrebidPlugin";
import { getWikiTargetedVideoInfo } from "../../helpers/hasVideoOnPage";
import { isBidderAdUnit } from "../../config/platforms/types";

type Ortb2ImpData = NonNullable<PrebidAdUnit["ortb2Imp"]>;

export type JWPlayerRTDPrebidPluginConfig = Pick<
  InstantConfigRepository,
  "icFeaturedVideoPlayer"
>;

export class JWPlayerRTDPrebidPlugin extends AdEnginePlugin {
  private readonly JWP_RTD_AUCTION_DELAY = 100;

  static isEnabled(config: JWPlayerRTDPrebidPluginConfig): boolean {
    return (
      !!window.canPlayVideo?.() &&
      config.icFeaturedVideoPlayer === "jwp-video-player"
    );
  }

  private configureJwpRtd(): Pick<PrebidConfig, "realTimeData"> {
    const mediaId = this.getMediaId();

    return {
      realTimeData: {
        auctionDelay: this.JWP_RTD_AUCTION_DELAY,
        dataProviders: [
          {
            name: "jwplayer",
            waitForIt: true,
            params: {
              mediaIDs: mediaId ? [mediaId] : [],
            },
          },
        ],
      },
    };
  }

  private extendOrtbWithJwpRtdData(ortb2Imp: Ortb2ImpData = {}): Ortb2ImpData {
    const mediaId = this.getMediaId();

    if (!mediaId) {
      return ortb2Imp;
    }

    return {
      ...ortb2Imp,
      ext: {
        ...ortb2Imp.ext,
        data: {
          ...(ortb2Imp.ext?.data ?? {}),
          jwTargeting: {
            mediaID: mediaId,
          },
        },
      },
    };
  }

  /**
   * Retrieves the media ID of the currently queued video.
   *
   * This method first attempts to obtain the media ID from the global `jwplayer` instance,
   * if it is available. If not found, it falls back to extracting the media ID from the
   * featured video configuration via `getWikiTargetedVideoInfo()`.
   *
   * @returns {string | undefined} The media ID if found, otherwise `undefined`.
   */
  private getMediaId(): string | undefined {
    if (typeof jwplayer !== "undefined") {
      const mediaId = jwplayer().getPlaylistItem()?.mediaid;

      if (mediaId) {
        return mediaId;
      }
    }

    const videoInfo = getWikiTargetedVideoInfo();

    const mediaId = videoInfo?.playlist?.[0]?.mediaid;

    if (!mediaId) {
      console.warn(
        "JWPlayerRTDPrebidPlugin: No mediaId found in the featured video config.",
      );
    }
    return mediaId;
  }

  hooks: Hooks = {
    onBiddersInit: () => {
      const prebidPlugin = this.usePlugin(PrebidPlugin);
      prebidPlugin?.addPrebidReadyCallback((pbjs: Prebid) => {
        pbjs.mergeConfig(this.configureJwpRtd());
      });
    },
    onCreateBidderAdUnit: (
      bidderSource: BidderSource,
      auctionData: AuctionData,
      adUnits: BidderAdUnitConfigMap[BidderSource],
    ) => {
      if (isBidderAdUnit("prebid", bidderSource, adUnits)) {
        adUnits.forEach((adUnit) => {
          if (adUnit.mediaTypes?.video) {
            adUnit.ortb2Imp = this.extendOrtbWithJwpRtdData(adUnit.ortb2Imp);
          }
        });
      }
    },
  };
}
